import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { copyText } from '../../lib/clipboard'
import { toast } from '../../lib/toast'
import { Glyph } from './Icon'

/** An email address that copies itself on click, with a toast and an inline tick. */
export function EmailCopy({ email, className = '' }: { email: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef(0)
  const textRef = useRef<HTMLSpanElement>(null)
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const onClick = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const ok = await copyText(email)
    if (ok) {
      setCopied(true)
      toast('Email copied')
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setCopied(false), 2000)
    } else {
      // Couldn't copy (very locked-down browsers): select it so the visitor can copy by hand.
      const el = textRef.current
      const sel = window.getSelection()
      if (el && sel) {
        const r = document.createRange()
        r.selectNodeContents(el)
        sel.removeAllRanges()
        sel.addRange(r)
      }
      toast('Press Ctrl+C or ⌘C to copy')
    }
  }

  return (
    <button type="button" className={`email-copy${copied ? ' copied' : ''}${className ? ` ${className}` : ''}`} onClick={onClick} aria-label={`Copy email address ${email}`}>
      <Glyph name="mail" />
      <span ref={textRef} className="email-text">{email}</span>
      <span className="email-act" aria-hidden="true"><Glyph name={copied ? 'check' : 'copy'} width={2} /></span>
    </button>
  )
}
